import styles from './PurchaseTypeSelector.module.css';

import type { FC } from 'react';

import React from 'react';
import classNames from 'classnames';
import { Lightning, ShieldCheck } from '@phosphor-icons/react';

import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';

export type PurchaseType = 'escrow' | 'direct';

interface PurchaseTypeSelectorProps {
  selectedType: PurchaseType;
  onSelect: (type: PurchaseType) => void;
  /**
   * Why the escrow purchase cannot be chosen by this buyer (app#176) — read from the chain registry
   * by the screen. When set, the escrow card is shown but not selectable, the sentence takes the
   * place of its caption, and Buy Now stays the buyer's way to pay.
   */
  escrowUnavailable?: string;
}

/** Enter and Space choose a card from the keyboard, as they do a native radio. */
const chooseOnKey = (choose: () => void) => (event: React.KeyboardEvent<HTMLDivElement>) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    choose();
  }
};

/**
 * Two cards, one chosen: a radio group. The action under them says just "Buy" (2026-10-24), so
 * the chosen mode must be readable off the cards themselves — `aria-checked` is what a screen
 * reader announces and what the e2e reads; the `selected` class is only how it looks.
 */
export const PurchaseTypeSelector: FC<PurchaseTypeSelectorProps> = ({
  selectedType,
  onSelect,
  escrowUnavailable,
}) => {
  const escrowDisabled = Boolean(escrowUnavailable);
  const escrowChosen = selectedType === 'escrow' && !escrowDisabled;
  const chooseEscrow = () => {
    if (!escrowDisabled) onSelect('escrow');
  };
  const chooseDirect = () => onSelect('direct');

  return (
    <Stack gap={2} ph={4} role="radiogroup" aria-label="How to buy">
      <div
        className={classNames(styles.card, {
          [styles.selected]: escrowChosen,
          [styles.disabled]: escrowDisabled,
        })}
        role="radio"
        aria-checked={escrowChosen}
        aria-disabled={escrowDisabled || undefined}
        tabIndex={escrowDisabled ? -1 : 0}
        onClick={chooseEscrow}
        onKeyDown={chooseOnKey(chooseEscrow)}
      >
        <Stack flexDirection="row" gap={2} alignItems="center">
          <ShieldCheck size={28} color="white" weight="fill" />
          <Stack gap={1}>
            <Typography color="white" fontWeight="600">
              Buy with Delivery
            </Typography>
            <Typography color="lightWhite" variant="small">
              {escrowUnavailable ?? 'Escrow-protected: delivery, survey and insurance services.'}
            </Typography>
          </Stack>
        </Stack>
      </div>

      <div
        className={classNames(styles.card, { [styles.selected]: selectedType === 'direct' })}
        role="radio"
        aria-checked={selectedType === 'direct'}
        tabIndex={0}
        onClick={chooseDirect}
        onKeyDown={chooseOnKey(chooseDirect)}
      >
        <Stack flexDirection="row" gap={2} alignItems="center">
          <Lightning size={28} color="white" weight="fill" />
          <Stack gap={1}>
            <Typography color="white" fontWeight="600">
              Buy Now
            </Typography>
            <Typography color="lightWhite" variant="small">
              Direct payment to the seller. No escrow — for in-person dealership purchases.
            </Typography>
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
};
